import React, { useState, useEffect } from 'react';
import { useSearchHighlight } from '../hooks/useSearchHighlight';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import impressumData from '../data/pages/impressum.json';
import * as S from '../styles/pages/BasePage.styles';

// Spam-Schutz: E-Mail mit Click-to-Reveal & Copy
const ProtectedEmail: React.FC<{ colors: any; language: string }> = ({ colors, language }) => {
  const [revealed, setRevealed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const email = 'hello@kamana.at';

  const revealEmail = () => {
    navigator.clipboard.writeText(email);
    setRevealed(true);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <>
      {revealed ? (
        <S.RevealedContent colors={colors}>
          {email}
        </S.RevealedContent>
      ) : (
        <>
          <S.RevealButton colors={colors} onClick={revealEmail}>
            {impressumData[language as 'de' | 'en'].revealButton.show}
          </S.RevealButton>
          {copySuccess && (
            <S.SuccessMessage colors={colors} show={copySuccess}>
              {impressumData[language as 'de' | 'en'].success.emailCopied}
            </S.SuccessMessage>
          )}
        </>
      )}
    </>
  );
};

// Spam-Schutz: Telefonnummer mit Click-to-Reveal & Copy
const ProtectedPhone: React.FC<{ colors: any; language: string }> = ({ colors, language }) => {
  const [revealed, setRevealed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const phone = '+43 (0) 650 3903381';

  const revealPhone = () => {
    navigator.clipboard.writeText(phone);
    setRevealed(true);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <>
      {revealed ? (
        <S.RevealedContent colors={colors}>
          {phone}
        </S.RevealedContent>
      ) : (
        <>
          <S.RevealButton colors={colors} onClick={revealPhone}>
            {impressumData[language as 'de' | 'en'].revealButton.show}
          </S.RevealButton>
          {copySuccess && (
            <S.SuccessMessage colors={colors} show={copySuccess}>
              {impressumData[language as 'de' | 'en'].success.phoneCopied}
            </S.SuccessMessage>
          )}
        </>
      )}
    </>
  );
};

// Spam-Schutz: Adresse mit Click-to-Reveal
const ProtectedAddress: React.FC<{ colors: any; language: string }> = ({ colors, language }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <>
      {revealed ? (
        <div>
          <S.AddressParagraph colors={colors} style={{ marginTop: '0', marginBottom: '0' }}>
            Katrin Sattler, BA, MSc (WU)
          </S.AddressParagraph>
          <S.AddressParagraph colors={colors} style={{ marginBottom: '0' }}>
            Wienerstrasse 110
          </S.AddressParagraph>
          <S.AddressParagraph colors={colors} style={{ marginBottom: '0' }}>
            8605 Kapfenberg
          </S.AddressParagraph>
          <S.AddressParagraph colors={colors} style={{ marginBottom: '0' }}>
            Österreich
          </S.AddressParagraph>
        </div>
      ) : (
        <S.RevealButton colors={colors} onClick={() => setRevealed(true)}>
          {impressumData[language as 'de' | 'en'].revealButton.showOnly}
        </S.RevealButton>
      )}
    </>
  );
};

const Impressum: React.FC = () => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  useSearchHighlight();
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  
  const data = impressumData[language];

  return (
    <S.PageWrapper colors={colors}>
      <S.Section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.Container>
          <S.PageTitle colors={colors}>
            {data.title}
          </S.PageTitle>
          
          <S.Paragraph colors={colors} data-text-element="true">
            {data.intro}
          </S.Paragraph>

          <S.Paragraph colors={colors} style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '20px', marginTop: '30px' }} data-text-element="true">
            {data.companyName}
          </S.Paragraph>

          <S.ContactRow>
            <span>{data.contact.email}</span>
            <ProtectedEmail colors={colors} language={language} />
          </S.ContactRow>

          <S.ContactRow>
            <span>{data.contact.phone}</span>
            <ProtectedPhone colors={colors} language={language} />
          </S.ContactRow>

          <S.ContactRow>
            <span>{data.contact.address}</span>
            <ProtectedAddress colors={colors} language={language} />
          </S.ContactRow>

          <S.Paragraph colors={colors} data-text-element="true">
            {data.companyInfo.business}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.companyInfo.member}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.companyInfo.authority}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.companyInfo.profession}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.companyInfo.country}
          </S.Paragraph>

          <S.SectionTitle colors={colors}>
            {data.mediaOwner.title}
          </S.SectionTitle>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.mediaOwner.name}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.mediaOwner.editorial}
          </S.Paragraph>

          <S.SectionTitle colors={colors}>
            {data.liability.title}
          </S.SectionTitle>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.liability.content1}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.liability.content2}
          </S.Paragraph>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.liability.content3}
          </S.Paragraph>

          <S.SectionTitle colors={colors}>
            {data.copyright.title}
          </S.SectionTitle>
          <S.Paragraph colors={colors} data-text-element="true">
            {data.copyright.content}
          </S.Paragraph>
          
          <S.Paragraph colors={colors} style={{ fontSize: '0.9rem', marginTop: '30px', marginBottom: '60px' }} data-text-element="true">
            {data.source}{' '}
            <S.StyledLink colors={colors} href="https://www.adsimple.at/" target="_blank" rel="noopener noreferrer">
              adsimple.at
            </S.StyledLink>
          </S.Paragraph>
        </S.Container>
      </S.Section>
    </S.PageWrapper>
  );
};

export default Impressum;
